/**
 * THE DAY AS LANES — the pure half of the Daily Overview timeline.
 *
 * One lane per operator, each trip a bar from its pickup to the API's
 * estimated end (`estimated_end`, sent when the screen asks for it with
 * `with_estimated_end=true`). Everything here is arithmetic over the
 * booking as `/bookings` returns it; what a trip's status MEANS is not
 * decided here but read from `tripProgress` (the twin of the dispatcher
 * API's attention rule) and the portal's own progress helpers, so a bar and
 * the dispatch board can never disagree about which trips are late.
 *
 * THE END OF A TRIP IS THE API'S, NEVER A GUESS MADE HERE. Where
 * `estimated_end` is absent or null — an API that does not send it yet, a
 * booking it could not estimate, or a leg it does not describe (see
 * `tripWindow`) — the end is UNKNOWN: the bar is drawn at
 * its minimum width with an open end, and the trip takes no part in the
 * overlap check. A second copy of the duration model in the browser would be
 * a second thing to change when the model changes.
 *
 * Positions are epoch milliseconds until the last step. The axis is laid on
 * Budapest hours, and because Budapest's offset is always a whole number of
 * hours, a UTC hour boundary IS a Budapest hour boundary — so flooring an
 * instant to the hour needs no zone at all.
 */
import dayjs from 'dayjs';
import {
  DEFAULT_TIMEZONE, formatDate, formatTime, getBookingDayjs,
} from '../../Utils/timezone';
import { addDays, isFinished, progressIndex } from '../Portal/portalFormat';
import { plural, shortDay } from '../Portal/Operator/operatorFormat';
import {
  assignmentsOf, attentionFor, crewOf, needsDriver, pastPickup, rowNeedsAttention,
} from './tripProgress';

/**
 * 'HH:mm' of an instant on the booking's own clock — its stored timezone,
 * else Budapest — which is the time the Table and the booking form show for
 * it. Only the axis itself is Budapest's.
 */
export const tripClock = (trip, value) => (
  getBookingDayjs(value, trip?.timezone || DEFAULT_TIMEZONE)?.format('HH:mm') || ''
);

/** Width of one hour on the axis. */
export const HOUR_PX = 104;

/** A bar is never narrower than this, so its time and name fit. */
export const MIN_BAR_PX = 92;

/** Height of one sub-row of a lane. */
export const ROW_PX = 48;

const BAR_GAP_PX = 4;
const HOUR_MS = 60 * 60 * 1000;

/** The day on screen opens at 06:00 and runs to midnight, unless a trip is outside it. */
const DAY_OPENS = '06:00';

const budapestInstant = (day, hhmm) => dayjs.tz(`${day} ${hhmm}`, DEFAULT_TIMEZONE).valueOf();

const instant = (value) => {
  const ms = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(ms) ? ms : null;
};

/**
 * `{ start, end, late, isReturn }` in epoch ms: the pickup, the API's
 * typical end and its late end. `end` and `late` are null when the API did
 * not estimate them — `estimated_end` absent or null — or sent one that does
 * not come after what precedes it.
 *
 * `day` is the Budapest day on screen. `/bookings` matches a day by a
 * booking's RETURN as well as its pickup, and `estimated_end` only ever
 * describes the leg starting at `date_datetime`; so a booking on screen
 * only for its return is drawn at the return (`isReturn`), with its end
 * unknown, rather than wearing the outbound's estimate on another day.
 *
 * A RETURN ROW HAS NO LIVE STATE ON THIS SCREEN. `driver_status` is one
 * record per booking and belongs to the outbound, `attentionFor` measures
 * lateness from `date_datetime`, and the nightly auto-complete counts 48
 * hours from the outbound too — so a return can be "Completed" before it
 * has run. None of that says anything about the return, and nothing tracks
 * the return itself, so its row is drawn as upcoming, kept out of the
 * late/waiting/no-show flags, and shown no progress (`barLook`, the "Need
 * attention" chip, TripPanel). Who is on it is still the booking's: a
 * return with nobody to drive it is still a gap.
 */
export const tripWindow = (trip, day = '') => {
  const pickup = instant(trip?.date_datetime);
  const back = instant(trip?.return_date_datetime);
  const onDay = (ms) => ms !== null && formatDate(ms) === day;

  if (day && onDay(back) && !onDay(pickup)) {
    return {
      start: back, end: null, late: null, isReturn: true,
    };
  }

  const typical = instant(trip?.estimated_end?.typical);
  const end = pickup !== null && typical !== null && typical > pickup ? typical : null;
  const late = instant(trip?.estimated_end?.late);

  return {
    start: pickup,
    end,
    late: end !== null && late !== null && late > end ? late : null,
    isReturn: false,
  };
};

/**
 * The axis, `{ from, to }` in epoch ms on whole hours: 06:00 to midnight of
 * the Budapest `day` ('YYYY-MM-DD'), widened to take in any trip outside it.
 * Without a day (the linked-bookings search, which has no date) it is just
 * the span of the trips. Null when there is nothing to draw.
 */
export const axisFor = (windows, day) => {
  const bounds = day ? [budapestInstant(day, DAY_OPENS), budapestInstant(addDays(day, 1), '00:00')] : [];
  const minimum = (MIN_BAR_PX / HOUR_PX) * HOUR_MS;

  windows.forEach(({ start, end, late }) => {
    if (start !== null) bounds.push(start, late ?? end ?? start + minimum);
  });

  if (!bounds.length) return null;

  return {
    from: Math.floor(Math.min(...bounds) / HOUR_MS) * HOUR_MS,
    to: Math.ceil(Math.max(...bounds) / HOUR_MS) * HOUR_MS,
  };
};

/** Where an instant sits on the axis, in px from its start. */
export const xOf = (axis, ms) => Math.round(((ms - axis.from) / HOUR_MS) * HOUR_PX);

/**
 * The hour marks along the top. An axis that runs into another day labels
 * its midnights with the date, so a linked booking next week is not read as
 * a trip tonight.
 */
export const hourTicks = (axis) => {
  const spansDays = formatDate(axis.from) !== formatDate(axis.to - 1);
  const ticks = [];

  for (let at = axis.from; at < axis.to; at += HOUR_MS) {
    const time = formatTime(at);
    ticks.push({ at, x: xOf(axis, at), label: spansDays && time === '00:00' ? shortDay(at) : time });
  }

  return ticks;
};

/**
 * `{ left, width, tail, estimated }` in px. The bar runs from the pickup to
 * the typical end, never narrower than MIN_BAR_PX; the tail is the stretch
 * from where the bar stops to the late end. `estimated: false` is a bar
 * whose end is unknown.
 */
export const barGeometry = ({ start, end, late }, axis) => {
  const left = xOf(axis, start);
  const width = Math.max(MIN_BAR_PX, end === null ? 0 : xOf(axis, end) - left);

  return {
    left,
    width,
    tail: late === null ? 0 : Math.max(0, xOf(axis, late) - (left + width)),
    estimated: end !== null,
  };
};

/**
 * The sub-row each span goes in, for spans in order of `left`: the first row
 * whose last bar has ended, else a new one. By the DRAWN extent — a short
 * trip still takes its minimum width — so two labels never sit on top of
 * each other. The faint late tail is not counted: it runs underneath.
 */
export const packRows = (spans) => {
  const ends = [];

  return spans.map(({ left, width }) => {
    let row = ends.findIndex((end) => end <= left);

    if (row === -1) {
      row = ends.length;
      ends.push(0);
    }

    ends[row] = left + width + BAR_GAP_PX;
    return row;
  });
};

/** A driver's or guide's name, as the lists show it. */
export const personName = (person) => person?.full_name || person?.name || '';

export const NO_OPERATOR_LANE = 'no-operator';

/**
 * `[{ key, name, unassigned, placements: [{ trip, entry }] }]` — one lane
 * per operator on the given (timed) trips, in the order the day unfolds,
 * with "No operator" first when anything is in it and absent otherwise.
 *
 * A booking with two operators (a guide and a driver) is placed in BOTH
 * lanes; one operator holding two of its slots is placed once, by the slot
 * that names a driver.
 *
 * "When" is where the bar starts on `day` (`tripWindow`) — the return, for
 * a booking on the day only for its return — so a lane is ordered, and its
 * bars are packed, by what is drawn rather than by an outbound on another day.
 */
export const buildLanes = (trips, day = '') => {
  const lanes = new Map();
  const start = (trip) => tripWindow(trip, day).start ?? 0;

  // Lanes are created as their first trip comes past, so walking the day in
  // start order IS ordering the lanes by first start.
  [...trips].sort((a, b) => start(a) - start(b)).forEach((trip) => {
    const crew = crewOf(trip);

    (crew.length ? crew : [null]).forEach((entry) => {
      const key = entry ? entry.operatorId : NO_OPERATOR_LANE;

      if (!lanes.has(key)) {
        lanes.set(key, {
          key,
          name: entry ? (entry.operator?.name || 'Unknown operator') : 'No operator',
          unassigned: !entry,
          placements: [],
        });
      }

      const { placements } = lanes.get(key);
      const placed = placements.find((placement) => placement.trip === trip);

      if (!placed) placements.push({ trip, entry });
      else if (!placed.entry.driverId && entry.driverId) placed.entry = entry;
    });
  });

  const list = [...lanes.values()];
  return [...list.filter((lane) => lane.unassigned), ...list.filter((lane) => !lane.unassigned)];
};

/** '3 trips · Kovács Béla', '5 trips · 2 drivers', '1 trip'. */
export const laneSummary = (lane) => {
  const trips = plural(lane.placements.length, 'trip', 'trips');
  const driven = lane.placements.filter(({ entry }) => entry?.driverId);
  const drivers = new Set(driven.map(({ entry }) => entry.driverId));

  if (drivers.size === 1) return `${trips} · ${personName(driven[0].entry.driver) || '1 driver'}`;
  if (drivers.size > 1) return `${trips} · ${drivers.size} drivers`;
  return trips;
};

/**
 * THE SAME DRIVER ON TWO TRIPS AT ONCE: `[{ driverId, a, b, from, to }]`,
 * where `from`–`to` is the stretch both trips claim.
 *
 * "Same" is the driver's id, never the name. A trip's window is its pickup
 * to its TYPICAL end — the late tail is a risk, not a booking — and a trip
 * with no estimated end is left out: an unknown end cannot be said to
 * overlap anything. Two trips that merely touch (one ends as the next
 * begins) do not overlap. A cancelled trip is not going to happen.
 */
export const driverOverlaps = (trips, day = '') => {
  const runsByDriver = new Map();

  trips.forEach((trip) => {
    const { start, end } = tripWindow(trip, day);
    if (start === null || end === null || trip?.status === 'Cancelled') return;

    new Set(assignmentsOf(trip).map((entry) => entry.driverId).filter(Boolean)).forEach((id) => {
      runsByDriver.set(id, [...(runsByDriver.get(id) || []), { trip, start, end }]);
    });
  });

  const found = [];

  runsByDriver.forEach((runs, driverId) => {
    runs.forEach((a, i) => runs.slice(i + 1).forEach((b) => {
      if (a.start < b.end && b.start < a.end) {
        found.push({
          driverId,
          a: a.trip,
          b: b.trip,
          from: Math.max(a.start, b.start),
          to: Math.min(a.end, b.end),
        });
      }
    }));
  });

  return found;
};

/**
 * The filter chips above the timeline. A chip FADES what does not match
 * rather than removing it, so the day keeps its shape. "Need attention" is
 * `rowNeedsAttention` — the rule the dispatch board's own count obeys —
 * except on a return row, which has no live state here (`tripWindow`) and
 * so needs attention only for a missing driver. `context` is
 * `{ now, day, overlapping }`, the last a Set of the trips in an overlap.
 */
export const CHIPS = [
  { key: 'all', label: 'All trips', test: () => true },
  {
    key: 'attention',
    label: 'Need attention',
    hot: true,
    test: (trip, { now, day }) => (
      tripWindow(trip, day).isReturn ? needsDriver(trip) : rowNeedsAttention(trip, now)
    ),
  },
  {
    key: 'no_operator', label: 'No operator', hot: true, test: (trip) => crewOf(trip).length === 0,
  },
  {
    key: 'no_driver', label: 'No driver named', test: (trip) => crewOf(trip).length > 0 && needsDriver(trip),
  },
  {
    key: 'overlap', label: 'Driver overlaps', hot: true, test: (trip, { overlapping }) => overlapping.has(trip),
  },
];

/**
 * What a bar looks like, by the rules the rest of the dashboard already
 * keeps (see the legend in DayTimeline for the words):
 *
 *   unassigned  — in the No-operator lane, whatever else is true of it
 *   upcoming    — a return row, whatever the outbound did (`tripWindow`)
 *   no-show     — reported by the driver on a booking still Confirmed; once
 *                 the office has set a no-show status itself it is settled
 *                 and reads as done
 *   late / waiting — `attentionFor`, the dispatcher API's twin
 *   off-app     — past its pickup with nothing tapped and NOT late: a
 *                 driver with no app login, a driver whose login the API
 *                 did not look up, no driver named, or an experience
 *                 (`attentionFor` infers lateness only from a driver on the
 *                 app). Not known to be under way, not known to be over, no
 *                 longer upcoming — just invisible to us
 *   done        — dropped off, Completed or Cancelled
 *   live        — the driver has tapped a step
 *   upcoming    — everything else
 */
export const barLook = (trip, now, { unassigned = false, isReturn = false } = {}) => {
  if (unassigned) return 'unassigned';
  if (isReturn) return 'upcoming';

  const attention = attentionFor(trip, now);

  if (attention === 'no_show') return trip?.status === 'Confirmed' ? 'no-show' : 'done';
  if (attention === 'late' || attention === 'waiting') return attention;
  if (attention === 'no_app') return 'off-app';
  if (isFinished(trip) || trip?.status === 'Cancelled') return 'done';
  if (progressIndex(trip) > 0) return 'live';
  // Past its pickup with nothing tapped, yet not late: nobody we know to be
  // on the app was ever going to tap (`attentionFor`).
  return pastPickup(trip, now) ? 'off-app' : 'upcoming';
};

/** The words for each look — the legend, and the end of every bar's spoken label. */
export const LOOK_LABELS = {
  done: 'Done',
  live: 'In progress',
  upcoming: 'Upcoming',
  late: 'Late',
  waiting: 'Driver waiting',
  'no-show': 'No-show',
  'off-app': 'Progress not visible',
  unassigned: 'No operator',
};
